import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SideBar from '../../components/SideBar/SideBar';
import TopNav from '../../components/TopNav/TopNav';
import api from '../../services/api';

// Profile photos the signup face check turned away, kept so a wrong call can
// be overruled. Built 2026-09-14 after a rejection left a real user with no
// photo at all: the check is one vision model's judgement, and replaying 13
// live profile photos through the old prompt rejected 7 of them.
//
// Separate from Verifications, which reviews the "Get verified" selfie against
// an existing profile photo. Different flow, different question.
const STATUS_LABEL = {
  pending: { text: 'Waiting on you', color: '#fbbf24' },
  approved: { text: 'You approved it', color: '#4ade80' },
  rejected: { text: 'You upheld the rejection', color: 'rgba(255,255,255,.5)' },
};

const TABS = [
  ['pending', 'Pending'],
  ['approved', 'Approved'],
  ['rejected', 'Upheld'],
];

const PhotoReview = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('pending');
  const [logs, setLogs] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [loadError, setLoadError] = useState(null);
  // Only the newest request may write state, so switching tabs quickly cannot
  // land one tab's rows under another tab's heading and its buttons.
  const reqSeq = useRef(0);

  // A failed load used to be console-only, which rendered as "Nothing waiting":
  // the queue was dead for six days behind that sentence (server #652).
  const load = async (which) => {
    const seq = (reqSeq.current += 1);
    setLoading(true);
    setLoadError(null);
    try {
      const res = await api.get(`/users/photo-moderation?status=${which}`);
      if (seq !== reqSeq.current) return;
      setLogs(Array.isArray(res.data?.logs) ? res.data.logs : []);
      setPendingCount(res.data?.pending || 0);
    } catch (err) {
      if (seq !== reqSeq.current) return;
      console.log(`error loading photo review queue: ${err}`);
      setLogs([]);
      // The badge is the one number that says the queue is alive, so it must not
      // keep asserting a count beside a panel saying nothing could be read.
      setPendingCount(0);
      setLoadError(err?.response?.data?.message || err?.message || 'Request failed');
    } finally {
      if (seq === reqSeq.current) setLoading(false);
    }
  };

  useEffect(() => { load(tab); }, [tab]);

  const decide = async (log, action) => {
    setBusyId(log._id);
    try {
      await api.post(`/users/photo-moderation/${log._id}/${action}`);
      setLogs((prev) => prev.filter((x) => x._id !== log._id));
      setPendingCount((n) => Math.max(0, n - 1));
    } catch (e) {
      alert(e?.response?.data?.message || 'Could not update. Please try again.');
    } finally {
      setBusyId(null);
    }
  };

  const fmt = (d) => {
    if (!d) return '';
    try { return new Date(d).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }); }
    catch { return ''; }
  };

  const emptyCopy = {
    pending: "Nothing waiting. Photos land here when the face check turns one down, so you can overrule it.",
    approved: "You haven't approved any rejected photos yet.",
    rejected: "You haven't upheld any rejections yet.",
  }[tab];

  return (
    <main>
      <TopNav />
      <div className="main-container">
        <SideBar />
        <article className="page-content">
          {/* width claimed explicitly: .page-content centers its children, so a
              section sized only by its content drags the whole page under the
              sidebar */}
          <section style={{ padding: '20px 2vw', width: '100%', maxWidth: 1500, boxSizing: 'border-box' }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: '#fff', margin: '0 0 4px' }}>Photo Review</h1>
            <p style={{ color: 'rgba(255,255,255,.45)', fontSize: 13, margin: '6px 0 0', maxWidth: 640 }}>
              Profile photos the face check turned down. Approving one makes it that person&apos;s
              profile photo straight away.
            </p>

            <div style={{ display: 'flex', gap: 8, margin: '16px 0 22px' }}>
              {TABS.map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setTab(key)}
                  style={{ borderRadius: 10, padding: '8px 16px', fontSize: 13, fontWeight: 700, cursor: 'pointer', border: tab === key ? 'none' : '1px solid rgba(255,255,255,.15)', background: tab === key ? 'linear-gradient(135deg,#0A3FD6,#7229F5)' : 'transparent', color: tab === key ? '#fff' : 'rgba(255,255,255,.6)' }}
                >
                  {label}{key === 'pending' && pendingCount > 0 ? ` · ${pendingCount}` : ''}
                </button>
              ))}
            </div>

            <p style={{ color: 'rgba(255,255,255,.45)', fontSize: 13, margin: '0 0 22px' }}>
              {loading ? 'Loading…' : loadError ? '' : `${logs.length} photo${logs.length === 1 ? '' : 's'}`}
            </p>

            {!loading && loadError && (
              <div style={{ background: 'rgba(229,62,62,0.08)', border: '1px solid rgba(229,62,62,0.35)', borderRadius: 14, padding: 28, textAlign: 'center', color: 'rgba(255,255,255,.75)' }}>
                <div style={{ marginBottom: 12 }}>Could not load the queue, so this is not the same as it being empty.</div>
                <div style={{ fontSize: 12, color: 'rgba(255,255,255,.45)', marginBottom: 16 }}>{loadError}</div>
                <button type="button" onClick={() => load(tab)} style={{ background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff', borderRadius: 10, padding: '8px 18px', cursor: 'pointer' }}>
                  Try again
                </button>
              </div>
            )}

            {!loading && !loadError && logs.length === 0 && (
              <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 40, textAlign: 'center', color: 'rgba(255,255,255,.4)' }}>
                {emptyCopy}
              </div>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {logs.map((log) => {
                const badge = STATUS_LABEL[log.status] || { text: log.status, color: 'rgba(255,255,255,.5)' };
                return (
                  <div key={log._id} style={{ display: 'flex', gap: 16, alignItems: 'center', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 14, padding: 16, flexWrap: 'wrap' }}>
                    {log.photo_unavailable ? (
                      // The image is still parked, we just could not sign a URL for
                      // it. Saying so stops this reading as "the photo is gone",
                      // which would invite upholding a rejection blind.
                      <div
                        title="The image is still stored, the panel could not load it"
                        style={{ width: 92, height: 92, borderRadius: 12, background: 'rgba(229,62,62,0.10)', border: '1px solid rgba(229,62,62,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', textAlign: 'center', fontSize: 11, lineHeight: 1.3, color: 'rgba(255,255,255,.7)', padding: 6 }}
                      >
                        Could not load
                      </div>
                    ) : (
                      <img
                        onClick={() => log.photo_url && setLightbox(log.photo_url)}
                        src={log.photo_url || ''}
                        alt="rejected upload"
                        title="The photo that was turned down"
                        style={{ width: 92, height: 92, borderRadius: 12, objectFit: 'cover', background: 'rgba(255,255,255,0.05)', cursor: log.photo_url ? 'pointer' : 'default' }}
                      />
                    )}
                    <div style={{ flex: 1, minWidth: 200 }}>
                      <div style={{ fontSize: 16, fontWeight: 600, color: '#fff' }}>{log.name || 'Unnamed'}</div>
                      <div style={{ marginTop: 6, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ color: badge.color, fontSize: 12, fontWeight: 700 }}>{badge.text}</span>
                        {log.had_photo_before && (
                          <span style={{ color: '#f87171', fontSize: 12, fontWeight: 700 }}>· cost them the photo they had</span>
                        )}
                      </div>
                      {log.ai_reason && (
                        <div style={{ color: 'rgba(255,255,255,.4)', fontSize: 12, marginTop: 6, maxWidth: 520 }}>{log.ai_reason}</div>
                      )}
                      <div style={{ color: 'rgba(255,255,255,.35)', fontSize: 11, marginTop: 6 }}>
                        {fmt(log.created_at)}
                        {log.reviewed_at ? ` · reviewed ${fmt(log.reviewed_at)}${log.reviewed_by ? ` by ${log.reviewed_by}` : ''}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <button type="button" onClick={() => navigate(`/users/edit/${log.user_id}`)} style={{ borderRadius: 10, padding: '9px 14px', fontSize: 12, fontWeight: 600, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(255,255,255,.15)', color: 'rgba(255,255,255,.7)' }}>View profile</button>
                      {log.status === 'pending' && (
                        <>
                          <button type="button" disabled={busyId === log._id} onClick={() => decide(log, 'reject')} style={{ borderRadius: 10, padding: '9px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', background: 'transparent', border: '1px solid rgba(248,113,113,.4)', color: '#f87171' }}>Uphold</button>
                          <button type="button" disabled={busyId === log._id} onClick={() => decide(log, 'approve')} style={{ borderRadius: 10, padding: '9px 16px', fontSize: 12, fontWeight: 700, cursor: 'pointer', border: 'none', background: 'linear-gradient(135deg,#0A3FD6,#7229F5)', color: '#fff' }}>{busyId === log._id ? '…' : '✓ Approve'}</button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        </article>
      </div>
      {lightbox && (
        <div onClick={() => setLightbox(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, cursor: 'zoom-out' }}>
          <img src={lightbox} alt="full size" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 12 }} />
        </div>
      )}
    </main>
  );
};

export default PhotoReview;
